const logger = require('meet-hour-logger').getLogger(__filename);

/**
 * Manages a queue of functions where the current function in progress will
 * automatically execute the next queued function.
 */
export class TaskQueue {
    /**
     * Creates a new instance of {@link TaskQueue} and sets initial instance
     * variable values.
     */
    constructor() {
        this._queue = [];
        this._currentTask = null;

        this._onTaskComplete = this._onTaskComplete.bind(this);
    }

    /**
     * Adds a new function to the queue. It will be immediately invoked if no
     * other functions are queued.
     *
     * @param {Function} taskFunction - The function to be queued for execution.
     * @private
     * @returns {void}
     */
    enqueue(taskFunction) {
        this._queue.push(taskFunction);
        this._executeNext();
    }

    /**
     * If no queued task is currently executing, invokes the first task in the
     * queue if any.
     *
     * @private
     * @returns {void}
     */
    _executeNext() {
        if (this._currentTask) {
            logger.warn('Task queued while a task is in progress.');

            return;
        }

        this._currentTask = this._queue.shift() || null;

        if (this._currentTask) {
            logger.debug('Executing a task.');

            try {
                this._currentTask(this._onTaskComplete);
            } catch (error) {
                logger.error(`Task execution failed: ${error}`);
                this._onTaskComplete();
            }
        }
    }

    /**
     * Prepares to invoke the next function in the queue.
     *
     * @private
     * @returns {void}
     */
    _onTaskComplete() {
        this._currentTask = null;
        logger.debug('Task completed.');
        this._executeNext();
    }

    /**
     * Discards every queued task that has not yet started. Tasks that are
     * currently running are not interrupted; the next call to
     * {@code _onTaskComplete} after this clear will find an empty queue and
     * exit without invoking anything.
     *
     * Intended for the conference tear-down path: a hangup that leaves pending
     * replaceLocalTrack tasks in this queue would otherwise drain them against
     * a torn-down conference, producing orphan tracks in the redux store and
     * no sender in the new conference after a leave-and-rejoin (a routine
     * pattern on Windows where the page is not reloaded).
     *
     * @returns {void}
     */
    clear() {
        const discarded = this._queue.length;

        this._queue = [];

        if (discarded > 0) {
            logger.debug(`TaskQueue: discarded ${discarded} pending task(s).`);
        }
    }
}
